import { apiClient } from "./client";

export interface ScrapeConfig {
  id?: number;
  platforms: string[];
  cron_schedule: string;
  detail_cron_schedule: string;
  detail_monitoring_enabled: boolean;
  duplicate_cron_schedule: string;
  duplicate_scan_enabled: boolean;
  max_listings_per_run: number;
  search_filters: Record<string, unknown> | null;
  last_run_at?: string | null;
  next_run_at?: string | null;
}

export interface ScrapeUrlItem {
  id?: number;
  label: string;
  category: string;
  parameters: Record<string, unknown>;
  is_active: boolean;
  url?: string | null;
  platform?: string;
}

export interface ScraperParameterDefinition {
  key: string;
  label: string;
  type: "text" | "url" | "password" | "number" | "boolean" | "select";
  required: boolean;
  description: string | null;
  placeholder: string | null;
  default: unknown;
  options: string[];
}

export interface ScraperCategory {
  category: string;
  label: string;
  parameters: ScraperParameterDefinition[];
}

/** Per-city scrape configuration. system_admin only. */
export const cityScrapeApi = {
  getZipcodes: async (cityId: number): Promise<string[]> => {
    const { data } = await apiClient.get(`/cities/${cityId}/zipcodes`);
    return data.zipcodes;
  },

  setZipcodes: async (cityId: number, zipcodes: string[]): Promise<void> => {
    await apiClient.put(`/cities/${cityId}/zipcodes`, { zipcodes });
  },

  getScrapeUrls: async (cityId: number): Promise<ScrapeUrlItem[]> => {
    const { data } = await apiClient.get(`/cities/${cityId}/scrape-urls`);
    return data;
  },

  getScraperCategories: async (cityId: number): Promise<ScraperCategory[]> => {
    const { data } = await apiClient.get(
      `/cities/${cityId}/scraper-categories`,
    );
    return data;
  },

  getScrapeUrl: async (
    cityId: number,
    sourceId: number,
  ): Promise<ScrapeUrlItem> => {
    const { data } = await apiClient.get(
      `/cities/${cityId}/scrape-urls/${sourceId}`,
    );
    return data;
  },

  createScrapeUrl: async (
    cityId: number,
    source: ScrapeUrlItem,
  ): Promise<ScrapeUrlItem> => {
    const { data } = await apiClient.post(
      `/cities/${cityId}/scrape-urls`,
      source,
    );
    return data;
  },

  setScrapeUrls: async (
    cityId: number,
    urls: ScrapeUrlItem[],
  ): Promise<ScrapeUrlItem[]> => {
    const { data } = await apiClient.put(`/cities/${cityId}/scrape-urls`, {
      urls,
    });
    return data;
  },

  setScrapeUrlSettings: async (
    cityId: number,
    sourceId: number,
    category: string,
    parameters: Record<string, unknown>,
  ): Promise<ScrapeUrlItem> => {
    const { data } = await apiClient.put(
      `/cities/${cityId}/scrape-urls/${sourceId}/settings`,
      {
        category,
        parameters,
      },
    );
    return data;
  },

  getScrapeConfig: async (cityId: number): Promise<ScrapeConfig | null> => {
    const { data } = await apiClient.get(`/cities/${cityId}/scrape-config`);
    return data;
  },

  setScrapeConfig: async (
    cityId: number,
    config: Omit<ScrapeConfig, "id" | "last_run_at" | "next_run_at">,
  ): Promise<ScrapeConfig> => {
    const { data } = await apiClient.put(
      `/cities/${cityId}/scrape-config`,
      config,
    );
    return data;
  },

  triggerScrape: async (cityId: number): Promise<void> => {
    await apiClient.post(`/cities/${cityId}/trigger-scrape`);
  },

  triggerMonitor: async (cityId: number): Promise<void> => {
    await apiClient.post(`/cities/${cityId}/trigger-monitor`);
  },

  triggerDuplicateScan: async (cityId: number): Promise<{ status: 'scan_started' | 'already_running' }> => {
    const response = await apiClient.post<{ status: 'scan_started' | 'already_running' }>(
      `/cities/${cityId}/trigger-duplicate-scan`,
    );
    return response.data;
  },
};
